import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { setAuth, setGeneralData } from 'ducks/auth/reducer'
import { logout as logoutUser, setUser } from 'ducks/user/reducer'
import { tokenSelector } from 'ducks/user'
import {
  INACTIVITY_TIMEOUT_MS,
  Routes,
  SESSION_TIMEOUT_EVENT_KEY,
  SESSION_TIMEOUT_MESSAGE,
  SESSION_TIMEOUT_MESSAGE_KEY,
} from 'constants/global.constants'
import { clearAuthStoragePreservingMfaDevice } from 'services/login/auth-storage'

const activityEvents: string[] = [
  'click',
  'keydown',
  'mousedown',
  'pointerdown',
  'scroll',
  'touchstart',
  'input',
  'submit',
]

const useSessionTimeout = (): void => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const token = useSelector(tokenSelector)
  const timeoutRef = useRef<number | null>(null)
  const isClosingSessionRef = useRef<boolean>(false)

  const clearTimer = useCallback((): void => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const clearAuthenticationState = useCallback((): void => {
    dispatch(setAuth({ role: '', isAuth: false }))
    dispatch(setGeneralData({}))
    dispatch(
      setUser({
        token: '',
        email: '',
        companyId: 0,
        userName: '',
        hasAdminPlatformManagement: false,
      }),
    )
    dispatch(logoutUser())
    clearAuthStoragePreservingMfaDevice()
  }, [dispatch])

  const closeSession = useCallback(
    (broadcast: boolean): void => {
      if (isClosingSessionRef.current) {
        return
      }

      isClosingSessionRef.current = true

      if (broadcast) {
        localStorage.setItem(SESSION_TIMEOUT_EVENT_KEY, String(Date.now()))
      }

      sessionStorage.setItem(SESSION_TIMEOUT_MESSAGE_KEY, SESSION_TIMEOUT_MESSAGE)
      clearTimer()
      clearAuthenticationState()
      navigate(Routes.login, { replace: true })
    },
    [clearAuthenticationState, clearTimer, navigate],
  )

  const resetTimer = useCallback((): void => {
    clearTimer()

    if (!token) {
      return
    }

    timeoutRef.current = window.setTimeout(() => {
      closeSession(true)
    }, INACTIVITY_TIMEOUT_MS)
  }, [clearTimer, closeSession, token])

  useEffect(() => {
    if (!token) {
      clearTimer()
      isClosingSessionRef.current = false
      return
    }

    const handleUserActivity = (): void => {
      if (!isClosingSessionRef.current) {
        resetTimer()
      }
    }

    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === SESSION_TIMEOUT_EVENT_KEY && event.newValue) {
        closeSession(false)
      }
    }

    isClosingSessionRef.current = false
    resetTimer()

    activityEvents.forEach((eventName: string) => {
      window.addEventListener(eventName, handleUserActivity, { passive: true })
    })
    window.addEventListener('storage', handleStorageChange)

    return () => {
      clearTimer()
      activityEvents.forEach((eventName: string) => {
        window.removeEventListener(eventName, handleUserActivity)
      })
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [clearTimer, closeSession, resetTimer, token])
}

export default useSessionTimeout
